<script lang="ts">
    import type { Signal } from '@preact/signals-react';
    import type { SymbolNotesService } from 'src/services/SymbolNotesService';

    interface Props {
        symbol: Signal<string | undefined>;
        notesService: SymbolNotesService;
    }

    let { symbol, notesService }: Props = $props();
    // svelte-ignore state_referenced_locally
    const { notes } = notesService;

    let isEditing = $state(false);
    let editValue = $state('');
    let inputRef = $state<HTMLTextAreaElement | undefined>(undefined);

    // Get the current note from the service (using $notes for Svelte store reactivity)
    const currentNote = $derived($symbol ? ($notes.get($symbol) ?? '') : '');

    // Truncate note for display (show first 50 chars)
    const displayNote = $derived(currentNote.length > 50 ? currentNote.slice(0, 50) + '...' : currentNote || '');

    function handleClick() {
        if (!$symbol) return;
        editValue = currentNote;
        isEditing = true;
        // Focus the input after the next tick
        setTimeout(() => inputRef?.focus(), 0);
    }

    function handleBlur() {
        if (!$symbol) return;
        isEditing = false;
        if (editValue !== currentNote) {
            notesService.updateNote($symbol, editValue);
        }
    }

    function handleKeyDown(e: KeyboardEvent) {
        // Stop propagation so ag-grid doesn't intercept keyboard shortcuts
        e.stopPropagation();

        if (e.key === 'Escape') {
            isEditing = false;
            editValue = currentNote;
        } else if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleBlur();
        }
    }
</script>

<div
    class="w-full h-full flex items-center"
    style="margin-left: calc(-1 * (var(--ag-cell-horizontal-padding) - 1px)); margin-right: calc(-1 * (var(--ag-cell-horizontal-padding) - 1px)); width: calc(100% + 2 * (var(--ag-cell-horizontal-padding) - 1px));"
>
    {#if isEditing}
        <textarea
            bind:this={inputRef}
            bind:value={editValue}
            onblur={handleBlur}
            onkeydown={handleKeyDown}
            class="w-full h-full px-2 text-xs bg-gray-700/50 text-white resize-none focus:outline-none border-0"
            placeholder="Add a note..."
        ></textarea>
    {:else}
        <button
            type="button"
            onclick={handleClick}
            class="w-full h-full px-2 text-left text-xs truncate cursor-pointer hover:text-jade-400 transition-colors {currentNote
                ? 'text-gray-300'
                : 'text-gray-500 italic'}"
        >
            {displayNote || 'Add note...'}
        </button>
    {/if}
</div>
